import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ApiError } from '../lib/iotApi';
import {
  Shield,
  Smartphone,
  Copy,
  Download,
  Check,
  ArrowRight,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * C21-R4-N5: Normalize TOTP errors to semantic codes.
 * Never renders raw response.data.detail or err.message directly.
 * Maps known backend responses to semantic codes; falls back to a generic
 * localized message for anything unrecognized.
 */
function normalizeTOTPError(err: unknown): string {
  // Already an ApiError with a semantic code — use it directly
  if (err instanceof ApiError) {
    return `errors:${err.semanticCode}`;
  }

  // Axios-style error with response body
  const detail: unknown = (err as any)?.response?.data?.detail;
  const status: number | undefined = (err as any)?.response?.status;

  // If the backend returned a structured code, use it
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const obj = detail as Record<string, unknown>;
    if (typeof obj.code === 'string' && obj.code.length > 0) {
      return `errors:${obj.code}`;
    }
  }

  // Map known backend string messages to semantic codes
  if (typeof detail === 'string') {
    const lower = detail.toLowerCase();
    if (lower.includes('invalid') && lower.includes('token')) return 'errors:totpInvalidToken';
    if (lower.includes('expired') && lower.includes('token')) return 'errors:totpExpiredToken';
    if (lower.includes('invalid') && (lower.includes('totp') || lower.includes('code'))) return 'errors:totpInvalidCode';
    if (lower.includes('already') && lower.includes('configured')) return 'errors:totpAlreadyConfigured';
  }

  // HTTP status-based mapping
  if (status === 401 || status === 403) return 'errors:totpInvalidToken';
  if (status === 422) return 'errors:totpInvalidCode';
  if (status && status >= 500) return 'errors:server_error';

  // Network error (no response)
  if ((err as any)?.code === 'ERR_NETWORK' || (err as any)?.message === 'Network Error') {
    return 'errors:networkError';
  }

  // Fallback — generic, localized
  return 'errors:unknown_error';
}

const TOTPSetupWizard: React.FC = () => {
  const { t } = useTranslation(['auth', 'common', 'errors']);
  const navigate = useNavigate();
  const { setupTOTP, verifyTOTP } = useAuth();
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [secret, setSecret] = useState('');
  const [otpauthUrl, setOtpauthUrl] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [otp, setOtp] = useState('');
  const [savedCodes, setSavedCodes] = useState(false);

  useEffect(() => {
    // Get temp token from sessionStorage
    const token = sessionStorage.getItem('totp_temp_token');
    if (!token) {
      navigate('/login');
      return;
    }
    setTempToken(token);
    
    // Auto-fetch TOTP setup data
    fetchTOTPSetup(token);
  }, []);

  const fetchTOTPSetup = async (token: string) => {
    try {
      setLoading(true);
      const data = await setupTOTP(token);
      setSecret(data.secret);
      setOtpauthUrl(data.otpauth_url);
      setRecoveryCodes(data.recovery_codes);
    } catch (error: any) {
      // C21-R4-N5: Display exactly one localized toast — never raw backend text
      const errorKey = normalizeTOTPError(error);
      toast.error(t(errorKey));
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp || otp.length !== 6) {
      toast.error(t('validation:otp_invalid'));
      return;
    }

    try {
      setLoading(true);
      await verifyTOTP(tempToken, otp);
      
      // Clear temp token
      sessionStorage.removeItem('totp_temp_token');
      
      // Navigate to step 3 to show recovery codes
      setStep(3);
    } catch (error: any) {
      // C21-R4-N5: Display exactly one localized toast — never raw backend text
      const errorKey = normalizeTOTPError(error);
      toast.error(t(errorKey));
    } finally {
      setLoading(false);
    }
  };

  const copySecret = () => {
    navigator.clipboard.writeText(secret);
    toast.success(t('auth:totp.secretCopied'));
  };

  const copyRecoveryCodes = () => {
    const codesText = recoveryCodes.join('\n');
    navigator.clipboard.writeText(codesText);
    toast.success(t('auth:totp.recoveryCodesCopied'));
  };

  const downloadRecoveryCodes = () => {
    const codesText = `Agri Safe Recovery Codes\n\nSave these codes in a secure location.\nEach code can only be used once.\n\n${recoveryCodes.join('\n')}\n\nGenerated: ${new Date().toISOString()}`;
    const blob = new Blob([codesText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'agrisafe-recovery-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(t('auth:totp.recoveryCodesDownloaded'));
  };

  const completeSetup = () => {
    // Navigate to admin dashboard for superadmin
    navigate('/admin');
  };

  if (loading && step === 1) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full space-y-8 p-8 bg-white rounded-xl shadow-lg">
        
        {/* Header */}
        <div className="text-center">
          <Shield className="mx-auto h-12 w-12 text-blue-600" />
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            {t('auth:totp.setupTitle')}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {t('auth:totp.setupSubtitle')}
          </p>
        </div>

        {/* Progress indicator */}
        <div className="flex items-center justify-center space-x-2">
          {[1, 2, 3].map((stepNum) => (
            <div
              key={stepNum}
              className={`w-3 h-3 rounded-full ${
                stepNum <= step ? 'bg-blue-600' : 'bg-gray-300'
              }`}
            />
          ))}
        </div>

        {/* Step 1: QR Code & Secret */}
        {step === 1 && (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-lg font-medium text-gray-900">
                {t('auth:totp.step1Title')}
              </h3>
              <p className="mt-2 text-sm text-gray-600">
                {t('auth:totp.step1Description')}
              </p>
            </div>

            {/* QR Code Placeholder - In production, use a QR library */}
            <div className="bg-gray-100 p-6 rounded-lg text-center">
              <div className="w-48 h-48 mx-auto bg-white border-2 border-gray-300 rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <Smartphone className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">QR Code</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {t('auth:totp.scanWithApp')}
                  </p>
                </div>
              </div>
            </div>

            {/* Manual Entry */}
            <div className="space-y-3">
              <p className="text-sm font-medium text-gray-700">
                {t('auth:totp.manualEntry')}
              </p>
              <div className="flex items-center space-x-2">
                <code className="flex-1 p-3 bg-gray-100 rounded-lg font-mono text-sm break-all">
                  {secret}
                </code>
                <button
                  onClick={copySecret}
                  className="p-2 text-gray-500 hover:text-gray-700 border border-gray-300 rounded-lg"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>

            <button
              onClick={() => setStep(2)}
              className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {t('auth:totp.continue')}
              <ArrowRight className="ml-2 w-4 h-4" />
            </button>
          </div>
        )}

        {/* Step 2: Verify OTP */}
        {step === 2 && (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-lg font-medium text-gray-900">
                {t('auth:totp.step2Title')}
              </h3>
              <p className="mt-2 text-sm text-gray-600">
                {t('auth:totp.step2Description')}
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('auth:totp.verificationCode')}
                </label>
                <input
                  id="otp"
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-center text-2xl font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  maxLength={6}
                  autoFocus
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && otp.length === 6) {
                      handleVerifyOTP();
                    }
                  }}
                />
              </div>

              <button
                onClick={handleVerifyOTP}
                disabled={loading || otp.length !== 6}
                className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {t('auth:totp.verifying')}
                  </>
                ) : (
                  <>
                    <Check className="mr-2 w-4 h-4" />
                    {t('auth:totp.verify')}
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Recovery Codes */}
        {step === 3 && (
          <div className="space-y-6">
            <div className="text-center">
              <Check className="mx-auto h-12 w-12 text-green-600" />
              <h3 className="mt-4 text-lg font-medium text-gray-900">
                {t('auth:totp.step3Title')}
              </h3>
              <p className="mt-2 text-sm text-gray-600">
                {t('auth:totp.step3Description')}
              </p>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex">
                <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
                <div className="ml-3">
                  <h4 className="text-sm font-medium text-yellow-800">
                    {t('auth:totp.recoveryCodesWarning')}
                  </h4>
                  <p className="mt-1 text-sm text-yellow-700">
                    {t('auth:totp.recoveryCodesDescription')}
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-2">
                  {recoveryCodes.map((code, index) => (
                    <code key={index} className="block p-2 bg-white rounded text-sm font-mono text-center">
                      {code}
                    </code>
                  ))}
                </div>
              </div>

              <div className="flex space-x-2">
                <button
                  onClick={copyRecoveryCodes}
                  className="flex-1 flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  <Copy className="mr-2 w-4 h-4" />
                  {t('auth:totp.copyRecoveryCodes')}
                </button>
                <button
                  onClick={downloadRecoveryCodes}
                  className="flex-1 flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  <Download className="mr-2 w-4 h-4" />
                  {t('auth:totp.downloadRecoveryCodes')}
                </button>
              </div>
            </div>

            <div className="flex items-center">
              <input
                id="saved-codes"
                type="checkbox"
                checked={savedCodes}
                onChange={(e) => setSavedCodes(e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="saved-codes" className="ml-2 block text-sm text-gray-900">
                {t('auth:totp.confirmSavedRecoveryCodes')}
              </label>
            </div>

            <button
              onClick={completeSetup}
              disabled={!savedCodes}
              className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Check className="mr-2 w-4 h-4" />
              {t('auth:totp.completeSetup')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TOTPSetupWizard;